import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Switch } from './ui/switch';
import { calculateLandTransferTax, getLTTInsights } from 'utils/mortgage-calculator/landTransferTax';
import { buildClassName } from 'utils/build-class-name';
import cardStyles from './ui/card.module.scss';
import styles from './LandTransferTaxCalculator.module.scss';

const LandTransferTaxCalculator = () => {
  const [homePrice, setHomePrice] = useState<number>(800000);
  const [location, setLocation] = useState<string>('toronto');
  const [isFirstTimeBuyer, setIsFirstTimeBuyer] = useState<boolean>(false);

  const result = calculateLandTransferTax(homePrice, location, isFirstTimeBuyer);
  const insights = getLTTInsights(homePrice, result, isFirstTimeBuyer);

  const getLocationLabel = (loc: string) => {
    switch (loc) {
      case 'toronto': return 'Toronto, ON';
      case 'ontario': return 'Ontario (outside Toronto)';
      case 'vancouver': return 'Vancouver, BC';
      case 'montreal': return 'Montreal, QC';
      default: return loc;
    }
  };

  return (
    <div className={styles.component}>
      <Card className={buildClassName(cardStyles.shadowElevated, styles.card)}>
        <CardHeader className={styles.header}>
          <CardTitle className={styles.title}>
            <span className={styles.icon}>🏠</span>
            Land Transfer Tax Calculator
          </CardTitle>
          <p className={styles.subtitle}>
            Calculate your land transfer tax and closing costs
          </p>
        </CardHeader>
        <CardContent className={styles.content}>
          <div className={styles.inputGrid}>
            <div className={styles.inputGroup}>
              <Label htmlFor="homePrice">Home Price ($)</Label>
              <Input
                id="homePrice"
                type="number"
                value={homePrice}
                onChange={e => setHomePrice(Number(e.target.value))}
                placeholder="800000"
                min="0"
              />
            </div>

            <div className={styles.inputGroup}>
              <Label htmlFor="location">Location</Label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger id="location">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="toronto">Toronto, ON</SelectItem>
                  <SelectItem value="ontario">Ontario (outside Toronto)</SelectItem>
                  <SelectItem value="vancouver">Vancouver, BC</SelectItem>
                  <SelectItem value="montreal">Montreal, QC</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className={styles.switchContainer}>
            <div className={styles.switchLabelGroup}>
              <Label htmlFor="firstTimeBuyer" className={styles.switchLabel}>
                First-Time Home Buyer?
              </Label>
              <p className={styles.switchDescription}>
                Qualify for rebates and exemptions
              </p>
            </div>
            <Switch
              id="firstTimeBuyer"
              checked={isFirstTimeBuyer}
              onCheckedChange={setIsFirstTimeBuyer}
            />
          </div>

          {/* Results Section */}
          <div className={styles.resultsSection}>
            <div className={styles.resultCard}>
              <div className={styles.resultHeader}>
                <div className={styles.resultLabel}>Total Land Transfer Tax</div>
                <div className={styles.resultAmount}>
                  ${result.netTax.toLocaleString()}
                </div>
                <div className={styles.resultLocation}>
                  for {getLocationLabel(location)}
                </div>
              </div>

              {/* Breakdown Table */}
              <div className={styles.breakdownList}>
                <div className={styles.breakdownItem}>
                  <span className={styles.breakdownLabel}>Provincial LTT</span>
                  <span className={styles.breakdownValue}>${result.provincialTax.toLocaleString()}</span>
                </div>

                {result.municipalTax > 0 && (
                  <div className={styles.breakdownItem}>
                    <span className={styles.breakdownLabel}>Municipal LTT (Toronto)</span>
                    <span className={styles.breakdownValue}>${result.municipalTax.toLocaleString()}</span>
                  </div>
                )}

                <div className={styles.breakdownItem}>
                  <span className={styles.breakdownLabel}>Subtotal Before Rebates</span>
                  <span className={styles.breakdownValue}>${result.totalTax.toLocaleString()}</span>
                </div>

                {(result.provincialRebate > 0 || result.municipalRebate > 0) && (
                  <>
                    {result.provincialRebate > 0 && (
                      <div className={styles.breakdownItem}>
                        <span className={styles.breakdownLabel}>Provincial Rebate</span>
                        <span className={buildClassName(styles.breakdownValue, styles.breakdownRebate)}>-${result.provincialRebate.toLocaleString()}</span>
                      </div>
                    )}
                    {result.municipalRebate > 0 && (
                      <div className={styles.breakdownItem}>
                        <span className={styles.breakdownLabel}>Municipal Rebate</span>
                        <span className={buildClassName(styles.breakdownValue, styles.breakdownRebate)}>-${result.municipalRebate.toLocaleString()}</span>
                      </div>
                    )}
                  </>
                )}

                <div className={styles.breakdownTotal}>
                  <span className={styles.breakdownTotalLabel}>Net Tax Payable</span>
                  <span className={styles.breakdownTotalValue}>${result.netTax.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Additional Cost Breakdown */}
            <div className={styles.costGrid}>
              <Card className={styles.costCard}>
                <CardContent className={styles.costContent}>
                  <div className={styles.costHeader}>
                    <span className={styles.costIcon}>📄</span>
                    <div className={styles.costLabel}>Legal Fees (est.)</div>
                  </div>
                  <div className={styles.costValue}>
                    ${(1500).toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              <Card className={styles.costCard}>
                <CardContent className={styles.costContent}>
                  <div className={styles.costHeader}>
                    <span className={styles.costIcon}>📄</span>
                    <div className={styles.costLabel}>Total Closing Costs (est.)</div>
                  </div>
                  <div className={buildClassName(styles.costValue, styles.costValuePrimary)}>
                    ${(result.netTax + 1500 + (homePrice * 0.01)).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Smart Insights Panel */}
      <Card className={buildClassName(cardStyles.shadowElevated, styles.insightsCard)}>
        <CardHeader className={styles.insightsHeader}>
          <CardTitle className={styles.insightsTitle}>
            <span className={styles.insightsIcon}>✨</span>
            Smart Insights
          </CardTitle>
        </CardHeader>
        <CardContent className={styles.insightsContent}>
          {insights.map((insight, index) => (
            <div
              key={index}
              className={styles.insightItem}
            >
              <p className={styles.insightText}>{insight}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default LandTransferTaxCalculator;
